/**
 * The rung gauge — one drawing, two scales, N rungs.
 *
 * The reference field drew exactly two crossings (proficient, then deep
 * mastery) because its ladder was fixed at two.  A registry ladder is
 * AUTHORED PER PLANET (`port.bars(planet)`), so this draws one arc per
 * rung, innermost first, and a planet with three rungs simply grows a
 * third ring.  The reference's two-rung look is the two-rung case of
 * this, unchanged.
 *
 * The `node` scale puts the first rung in the middle as a waxing PHASE
 * and the rest around it as rings; the sky scale draws every rung as a
 * ring.  Phase can never be confused with a ring, which is what keeps
 * "more filled" and "one more crossing" visually distinct.
 *
 * CREDIT vs PROOF.  A rung can be `met` on an assertion (a placement
 * grant) while the evidence behind it reads short — `progress` is the
 * evidence, `met` is what the sky shows.  The gap between them is drawn,
 * never hidden: the map would lie if it hid what a learner can do, and
 * it would lie differently if it drew borrowed ground as earned.
 */

import type { RungReading } from "./types";
import { arcPath, phasePath } from "./geometry";
import type { Palette } from "./palette";

export type CreditStyle = "hollow" | "hatched" | "split";

export const CREDIT_STYLES: [CreditStyle, string, string][] = [
  ["hollow", "hollow", "credit drawn unfilled — the sky looks lighter until it is earned"],
  ["hatched", "hatched", "credit drawn textured — keeps its weight, marked provisional"],
  ["split", "split", "solid to the proven mark, dotted for the rest"],
];

type Props = {
  rungs: RungReading[];
  R: number;
  colours: string[];
  P: Palette;
  node?: boolean;
  dim?: boolean;
  credit: CreditStyle;
  /** Non-scaling stroke props, threaded from the camera-scaled canvas. */
  nss: Record<string, unknown>;
  /** Stable prefix for this gauge's hatch pattern ids. */
  idPrefix: string;
};

const EPS = 0.004;

const colourFor = (colours: string[], index: number) => colours[index % colours.length];

export function Mastery({ rungs, R, colours, P, node, dim, credit, nss, idPrefix }: Props) {
  if (!rungs.length) return null;
  const dormant = P.lv[0];

  const sweep = (r: number, f: number) => arcPath(0, 0, r, -90, -90 + Math.min(359.6, 360 * f));
  const part = (r: number, a: number, b: number) =>
    arcPath(0, 0, r, -90 + 360 * a, -90 + Math.min(359.6, 360 * b));

  /** One rung's arc, drawn so it says where its proof runs out. */
  const gauge = (
    r: number,
    shown: number,
    proven: number,
    credited: boolean,
    colour: string,
    width: number,
    index: number,
  ) => {
    if (!credited || credit === "hatched") {
      return (
        <path
          d={sweep(r, shown)} fill="none"
          stroke={credited ? `url(#${idPrefix}hx${index})` : colour}
          strokeWidth={width} strokeLinecap={credited ? "butt" : "round"}
          strokeOpacity={dim ? 0.55 : 1} {...nss}
        />
      );
    }
    if (credit === "split") {
      return (
        <g>
          {proven > EPS && (
            <path
              d={sweep(r, proven)} fill="none" stroke={colour} strokeWidth={width}
              strokeLinecap="round" strokeOpacity={dim ? 0.55 : 1} {...nss}
            />
          )}
          <path
            d={part(r, proven, shown)} fill="none" stroke={colour} strokeWidth={width * 0.72}
            strokeLinecap="butt" strokeOpacity={dim ? 0.4 : 0.85}
            strokeDasharray={`${(width * 0.5).toFixed(2)} ${(width * 0.62).toFixed(2)}`} {...nss}
          />
        </g>
      );
    }
    return (
      <g>
        {[-1, 1].map((k) => (
          <path
            key={k} d={sweep(r + k * width * 0.36, shown)} fill="none" stroke={colour}
            strokeWidth={width * 0.26} strokeLinecap="round" strokeOpacity={dim ? 0.5 : 0.92} {...nss}
          />
        ))}
      </g>
    );
  };

  const read = (rung: RungReading) => {
    const shown = rung.met ? 1 : Math.max(0, Math.min(1, rung.progress));
    const proven = Math.max(0, Math.min(1, rung.progress));
    return { shown, proven, credited: shown - proven > EPS };
  };

  const hatches = (
    <defs>
      {rungs.map((_, index) => (
        <pattern
          key={index} id={`${idPrefix}hx${index}`} patternUnits="userSpaceOnUse"
          width="1.5" height="1.5" patternTransform="rotate(45)"
        >
          <rect width="1.5" height="1.5" fill={colourFor(colours, index)} fillOpacity="0.2" />
          <line x1="0" y1="0" x2="0" y2="1.5" stroke={colourFor(colours, index)} strokeWidth="0.68" />
        </pattern>
      ))}
    </defs>
  );

  if (node) {
    const [first, ...rest] = rungs;
    const { shown, proven, credited } = read(first);
    const colour = colourFor(colours, 0);
    return (
      <g pointerEvents="none">
        {hatches}
        <circle r={R} fill={P.night ? "#0A0F1C" : "#EFE6CE"} fillOpacity="0.92" />
        {shown > EPS &&
          (credited && credit === "hollow" ? (
            <path d={phasePath(R, shown)} fill="none" stroke={colour} strokeWidth={R * 0.11} strokeOpacity="0.95" {...nss} />
          ) : (
            <>
              <path
                d={phasePath(R, credited && credit === "split" ? proven : shown)}
                fill={credited && credit === "hatched" ? `url(#${idPrefix}hx0)` : colour}
                fillOpacity={dim ? 0.5 : 0.95}
              />
              {credited && credit === "split" && shown - proven > EPS && (
                <path
                  d={phasePath(R, shown)} fill="none" stroke={colour} strokeWidth={R * 0.09}
                  strokeOpacity="0.8" strokeDasharray={`${R * 0.24} ${R * 0.24}`} {...nss}
                />
              )}
            </>
          ))}
        <circle
          r={R} fill="none" stroke={shown > 0 ? colour : dormant}
          strokeOpacity={first.met ? (credited ? 0.7 : 1) : shown > 0 ? 0.6 : 0.45}
          strokeWidth={first.met && !credited ? R * 0.15 : R * 0.09}
          strokeDasharray={shown > 0 ? "none" : `${R * 0.22} ${R * 0.26}`}
          style={first.met && !credited ? { filter: `drop-shadow(0 0 ${R * 0.42}px ${colour}AA)` } : undefined}
          {...nss}
        />
        {rest.map((rung, i) => {
          const r = R * (1.58 + i * 0.42);
          const state = read(rung);
          const c = colourFor(colours, i + 1);
          return (
            <g key={rung.name}>
              <circle r={r} fill="none" stroke={dormant} strokeOpacity="0.32" strokeWidth={R * 0.075} {...nss} />
              {state.shown > EPS && (
                <g style={rung.met && !state.credited ? { filter: `drop-shadow(0 0 ${R * 0.55}px ${c})` } : undefined}>
                  {gauge(r, state.shown, state.proven, state.credited, c, R * 0.21, i + 1)}
                </g>
              )}
              {rung.met && !state.credited && (
                <circle r={r * 1.15} fill="none" stroke={c} strokeWidth={R * 0.075} strokeOpacity="0.8" {...nss} />
              )}
            </g>
          );
        })}
      </g>
    );
  }

  return (
    <g pointerEvents="none">
      {hatches}
      {rungs.map((rung, index) => {
        const r = R * (1 + index * 0.3);
        const state = read(rung);
        const colour = colourFor(colours, index);
        return (
          <g key={rung.name}>
            <circle
              r={r} fill="none" stroke={dormant}
              strokeOpacity={dim ? 0.2 - index * 0.03 : 0.34 - index * 0.05} strokeWidth="0.5" {...nss}
            />
            {state.shown > EPS && (
              <g style={rung.met && !state.credited ? { filter: `drop-shadow(0 0 2.4px ${colour}AA)` } : undefined}>
                {gauge(r, state.shown, state.proven, state.credited, colour, rung.met && !state.credited ? 1.5 : 1.05, index)}
              </g>
            )}
          </g>
        );
      })}
      {rungs.length > 0 && rungs[rungs.length - 1].met && (
        <circle
          r={R * (1 + (rungs.length - 1) * 0.3) * 1.24} fill="none"
          stroke={colourFor(colours, rungs.length - 1)} strokeWidth="0.4"
          strokeOpacity="0.6" strokeDasharray="0.6 1.9" {...nss}
        />
      )}
    </g>
  );
}
